import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import { decimalToPercent, round, toSqftWithoutAppend } from "../utils/operations";
import { getSpecialtyTypeDisplayNameNoPropertyType } from "../property/constants";
import operations from "./ducks/operations";
import includes from "lodash/includes";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import IconButton from "@mui/material/IconButton";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import BSInput from "../../../common/js/components/BSInput";

const ResearchSnapshotRow = (props) => {
    const { snapshot, columns, role, editResponse } = props;
    const [initialSnapshot, setInitialSnapshot] = useState({});
    const [newSnapshot, setNewSnapshot] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [disableCancel, setDisableCancel] = useState(false);
    const [disableSave, setDisableSave] = useState(false);
    const [open, setOpen] = useState(false);

    const cancelEditState = () => {
        setEditMode(false);
        setNewSnapshot(initialSnapshot);
    };
    const enterEditState = () => {
        setEditMode(true);
    };
    const exitEditState = () => {
        setEditMode(false);
    };
    const saveChanges = async () => {
        setDisableCancel(true);
        setDisableSave(true);
        const response = await operations.editExistingSnapshot(snapshot.researchSnapshotKey, newSnapshot);
        if (response.status >= 200 && response.status < 300) {
            editResponse(true);
            setInitialSnapshot(response.data);
            exitEditState();
        } else {
            editResponse(false);
        }
        setDisableCancel(false);
        setDisableSave(false);
    };
    const onChange = (e) => {
        // React funny business
        // https://stackoverflow.com/questions/44708518/event-target-is-null-inside-functional-setstate
        const { id, value } = e.target;
        setNewSnapshot((prevState) => ({ ...prevState, [id]: value }));
    };
    const cellValidation = (value, group) => {
        switch (group) {
            case "specialtyType":
                return getSpecialtyTypeDisplayNameNoPropertyType(value);
            case "lease":
                return value !== null ? round(value, 2) : "";
            case "percentage":
                return decimalToPercent(value, true);
            case "sqFt":
                return toSqftWithoutAppend(value);
            default:
                return value;
        }
    };

    useEffect(() => {
        setInitialSnapshot(snapshot);
        setNewSnapshot(snapshot);
    }, []);

    return (
        <React.Fragment>
            <TableRowStyle sx={{ "& > *": { borderBottom: "unset" } }}>
                {columns.map((col) => {
                    const { group, field, editable } = col;
                    switch (group) {
                        case "notes":
                            return (
                                <TableCellStyle align="center">
                                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                </TableCellStyle>
                            );
                        case "edit":
                            return (
                                <TableCellStyle align="center">
                                    {editMode ? (
                                        <div>
                                            <IconButton
                                                disabled={disableSave}
                                                aria-label="save edit state"
                                                onClick={saveChanges}
                                            >
                                                <SaveIconStyle/>
                                            </IconButton>
                                            <IconButton
                                                disabled={disableCancel}
                                                aria-label="cancel edit state"
                                                onClick={cancelEditState}
                                            >
                                                <CancelIconStyle/>
                                            </IconButton>
                                        </div>
                                    ) : (
                                        <IconButton aria-label="enter exit state" size="small" onClick={enterEditState}>
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </TableCellStyle>
                            );

                        default:
                            const value = cellValidation(newSnapshot[field], group);
                            const cellValue =
                                value === null ||
                                value === undefined ||
                                (typeof value === "string" && value.length === 0)
                                    ? "-"
                                    : value;
                            return (
                                <TableCellStyle align="center">
                                    {editMode &&
                                    editable &&
                                    includes(["research", "researchAdmin", "dataAdmin"], role) ? (
                                        <BSInput
                                            id={field}
                                            value={newSnapshot[field]}
                                            onChange={onChange}
                                            type={group === "sqFt" ? "number" : null}
                                            endAdornment={group === "percentage" ? "%" : null}
                                            extraInputStyles={{ borderColor: "#A0A6A0" }}
                                            noFullWidth={true}
                                        />
                                    ) : (
                                        cellValue
                                    )}
                                </TableCellStyle>
                            );
                    }
                })}
            </TableRowStyle>
            <CollapseContainer colSpan={50}>
                <Collapse in={open} timeout="auto">
                    {editMode ? (
                        <NoteEditMode
                            id={"notes"}
                            value={newSnapshot["notes"]}
                            onChange={onChange}
                        />
                    ) : (
                        <NotesContainer>{newSnapshot["notes"]}</NotesContainer>
                    )}
                </Collapse>
            </CollapseContainer>
        </React.Fragment>
    );
};

const TableRowStyle = styled(TableRow)({
    "&:nth-of-type(odd)": {
      backgroundColor: "#e7e6e6",
    },
    height: "50%",
});
  
const TableCellStyle = styled(TableCell)({
    whiteSpace: "nowrap",
});
  
const CollapseContainer = styled(TableCell)({
    paddingBottom: 0,
    paddingTop: 0,
    whiteSpace: "nowrap",
    border: "none",
    "&:nth-of-type(odd)": {
      backgroundColor: "#e7e6e6",
    },
    height: "50%",
});
  
const NotesContainer = styled('ul')({
    listStyleType: "circle",
    marginLeft: "5em",
    display: "list-item",
});
  
const NoteEditMode = styled(BSInput)({
    borderColor: "#A0A6A0",
});
  
const SaveIconStyle = styled(SaveIcon)({
    fontSize: "smaller",
});

const CancelIconStyle = styled(CancelIcon)({
    fontSize: "smaller",
});

export default ResearchSnapshotRow;
